import React from "react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Button } from "src/components/ui/button";
import { Badge } from "src/components/ui/badge";
import { ArrowRight, Clock, CheckCircle, Users } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";

const SituationStatus = ({ situation }) => {
  // Status types:
  // 1. Not started - no solution submitted
  // 2. Answered, waiting for others - has solution but not all responded
  // 3. Ready for evaluation - all responded, not evaluated
  // 4. Complete - answered and evaluated

  const getStatusDisplay = () => {
    if (!situation.isCompleted) {
      return {
        badge: (
          <Badge
            variant="secondary"
            className="bg-blue-50 text-blue-700 border-blue-200"
          >
            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2" />
            Nezačaté
          </Badge>
        ),
        buttonText: "Riešiť situáciu",
        buttonDisabled: false,
      };
    }

    if (situation.isCompleted && !situation.allResponded) {
      return {
        badge: (
          <Badge
            variant="outline"
            className="bg-yellow-50 text-yellow-700 border-yellow-200"
          >
            <Clock className="w-4 h-4 mr-1" />
            <span>Čakáme na odpovede</span>
          </Badge>
        ),
        buttonText: "Čakáme na ostatných",
        buttonDisabled: true,
      };
    }

    if (
      situation.isCompleted &&
      situation.allResponded &&
      !situation.isEvaluated
    ) {
      return {
        badge: (
          <Badge
            variant="secondary"
            className="bg-green-50 text-green-700 border-green-200 whitespace-nowrap min-w-fit px-3"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            <span>Pripravené na hodnotenie</span>
          </Badge>
        ),
        buttonText: "Hodnotiť riešenia",
        buttonDisabled: false,
      };
    }

    // All solutions evaluated
    return {
      badge: (
        <Badge
          variant="outline"
          className="bg-purple-50 text-purple-700 border-purple-200"
        >
          <CheckCircle className="w-4 h-4 mr-1" />
          Dokončené
        </Badge>
      ),
      buttonText: "Zobraziť výsledky",
      buttonDisabled: false,
    };
  };

  const status = getStatusDisplay();

  return (
    <Card
      className={`hover:shadow-md transition-all duration-300 ${
        situation.isCompleted && situation.allResponded
          ? "bg-green-50"
          : situation.isCompleted
          ? "bg-yellow-50"
          : "bg-white"
      }`}
    >
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-xl font-semibold text-gray-800 truncate">
            {situation.title}
          </CardTitle>
          {/* Debug badge */}
          {/* <div className="text-xs text-gray-500 mb-2">
            C: {situation.isCompleted ? "✅" : "❌"} | R:{" "}
            {situation.allResponded ? "✅" : "❌"} | E:{" "}
            {situation.isEvaluated ? "✅" : "❌"}
          </div> */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>{status.badge}</TooltipTrigger>
              <TooltipContent>
                <p>{getTooltipText(situation)}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent>
        <div
          className="text-gray-600 line-clamp-3 prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: situation.description }}
        />
      </CardContent>
      <CardFooter>
        <Button
          variant="outline"
          className="w-full justify-between text-base hover:bg-gray-100"
          disabled={status.buttonDisabled}
          onClick={() => situation.onAction(situation)}
        >
          <span>{status.buttonText}</span>
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </CardFooter>
    </Card>
  );
};

const getTooltipText = (situation) => {
  if (!situation.isCompleted) {
    return "Zatiaľ ste neodpovedali na túto situáciu";
  }
  if (!situation.allResponded) {
    return `Čakáme na odpovede od ${
      situation.totalCount - situation.respondedCount
    } účastníkov`;
  }
  if (!situation.isEvaluated) {
    return "Všetci odpovedali, môžete pristúpiť k hodnoteniu";
  }
  return "Všetky riešenia už boli ohodnotené";
};

export default SituationStatus;
